import type { ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { Controller, useFormContext } from 'react-hook-form';
import { $generateHtmlFromNodes } from '@lexical/html';
import { Pretendard } from '@/styles/fonts';
import { Box } from '@mui/material';
import EditorSkeleton from './EditorSkeleton';

const LexicalBoardEditor = dynamic(
  () => import('@/components/LexicalEditor/BoardEditor'),
  { ssr: false, loading: () => <EditorSkeleton /> },
);

/** 에디터를 띄우기 전(양식 응답 대기 등)에 코드 내려받기를 먼저 시작해 두는 용도 — 같은 청크라 중복 다운로드 없음 */
export const preloadLexicalEditor = () => {
  // 실패해도 실제 표시 시점에 dynamic 이 다시 받으므로 여기선 무시
  import('@/components/LexicalEditor/BoardEditor').catch(() => undefined);
};

type RHFLexicalEditorProps = {
  name: string;
  required?: boolean;
  isSimple?: boolean;
  placeholder?: string;
  defaultFontSize?: number;
  /** 에디터 composer 안에 렌더할 화면 전용 플러그인 */
  plugins?: ReactNode;
};

const RHFLexicalEditor = (props: RHFLexicalEditorProps) => {
  const { name, required, isSimple, placeholder, defaultFontSize, plugins } =
    props;
  const { control, watch } = useFormContext();
  const id = watch('id');

  return (
    <Box className={Pretendard.className}>
      <Controller
        key={id} // id가 변경될 때마다 새로 렌더링
        render={({ field }) => (
          <LexicalBoardEditor
            onChange={(editorState, editor) => {
              editor.update(() => {
                const htmlString = $generateHtmlFromNodes(editor, null);
                field.onChange(htmlString);
              });
            }}
            value={field.value}
            isSimple={isSimple}
            placeholder={placeholder}
            defaultFontSize={defaultFontSize}
            plugins={plugins}
          />
        )}
        name={name}
        control={control}
        defaultValue=""
        rules={{
          required: required ?? false,
        }}
      />
    </Box>
  );
};

export default RHFLexicalEditor;
